import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import type { Guid } from "@kiraah-shared/common";
import { MOBILE_BASE_PATH } from "../../shell/mobile-routes.constants";
import { MOBILE_MISSION_TASK_ROUTE_SEGMENTS, mobileMissionTasksUrl } from "../../mission-task/mobile-mission-task-route.provider";
import { createMobileMissionAppAccess } from "../../mission-task/helpers/mobile-mission-app-access";
import { MobileModuleAccessService } from "../../profile/services/mobile-module-access.service";
import { MobileVoicePurchaseRequestRecorderComponent } from "../mobile-voice-purchase-request-recorder/mobile-voice-purchase-request-recorder.component";
import { resolveMobileVoicePurchaseRequestErrorKey } from "../helpers/mobile-voice-purchase-request-error.helper";
import { MobileVoicePurchaseRequestService } from "../services/mobile-voice-purchase-request.service";

@Component({
  selector: "mobile-voice-purchase-request-workflow-page",
  standalone: true,
  imports: [CommonModule, TranslateModule, MobileVoicePurchaseRequestRecorderComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./mobile-voice-purchase-request-workflow-page.component.html",
  styleUrl: "./mobile-voice-purchase-request-workflow-page.component.scss",
})
export class MobileVoicePurchaseRequestWorkflowPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly moduleAccess = inject(MobileModuleAccessService);
  private readonly voicePurchaseRequestService = inject(MobileVoicePurchaseRequestService);
  private readonly missionNavSegments = inject(MOBILE_MISSION_TASK_ROUTE_SEGMENTS);

  protected readonly access = createMobileMissionAppAccess(this.moduleAccess, this.missionNavSegments).voiceReportAccess;

  protected readonly missionUid = signal<Guid | null>(null);
  protected readonly continueSessionUid = signal<Guid | null>(null);
  protected readonly missionLabel = signal<string | null>(null);
  protected readonly errorMessageKey = signal<string | null>(null);
  protected readonly isSubmitting = signal(false);

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.missionUid.set((params.get("missionUid") as Guid) ?? null);
    });

    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((queryParams) => {
      const label = queryParams.get("missionLabel");
      this.missionLabel.set(label?.trim() || null);
      const sessionUid = queryParams.get("sessionUid");
      this.continueSessionUid.set((sessionUid?.trim() as Guid) || null);
    });
  }

  protected onSent(rawText: string): void {
    const missionUid = this.missionUid();
    if (!missionUid || this.isSubmitting()) {
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessageKey.set(null);

    this.voicePurchaseRequestService
      .submitSession(missionUid, rawText, this.continueSessionUid())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (session) => {
          this.isSubmitting.set(false);
          void this.router.navigate(
            [MOBILE_BASE_PATH, ...this.missionNavSegments, missionUid, "voice-purchase-request", "result", session.Uid],
            {
              queryParams: {
                missionLabel: this.missionLabel() ?? undefined,
                regenerate: "1",
              },
            },
          );
        },
        error: (err: Error) => {
          this.isSubmitting.set(false);
          this.errorMessageKey.set(resolveMobileVoicePurchaseRequestErrorKey(err.message));
        },
      });
  }

  protected onRecordError(key: string): void {
    this.errorMessageKey.set(key);
  }

  protected backToList(): void {
    void this.router.navigateByUrl(mobileMissionTasksUrl(this.missionNavSegments));
  }

  protected errorMessage(): string {
    const key = this.errorMessageKey();
    if (!key) {
      return "";
    }
    return this.translate.instant(key);
  }

  protected clearError(): void {
    this.errorMessageKey.set(null);
  }
}
